/**
 * Stated-intent echo checks (Ashok ruling, 2026-07-25; rebased onto
 * echo_metadata, 2026-07-26): the intake agent now records the user's
 * stated procurement intent structure explicitly at intake time
 * (echo_metadata - memory/DESIGN-services-engagement-models.md) instead of
 * these rules re-parsing free text themselves. A VOLUME ("2 SAP
 * consultants") and a DURATION ("3 months") each get their own soft
 * consistency check against whatever the user later answers, attributed to
 * the STATED REQUIREMENT (never to a field the user typed), quoting the
 * ORIGINAL evidence text verbatim - since that's the anchor being echoed
 * back.
 *
 * Repro that motivated this (evidence, "I need 2 SAP consultants for 3
 * months"):
 *   - Volume intent (2 consultants): user changed the answered quantity
 *     2 -> 3 - no flag fired. SARV below fixes this.
 *   - Duration intent (3 months): engagement dates spanning only ~1 month
 *     fired the EXISTING typed-quantity-vs-dates dialog
 *     (StepAnswers.jsx's CALENDAR_DURATION_UNITS check) - but attributed
 *     to "Quantity" ("Quantity says 3 Months") rather than to the user's
 *     own stated requirement. SARD below fixes the wording; the de-dup
 *     helper at the bottom of this file stops the OLD dialog and SARD
 *     from both firing for the same fact.
 *
 * ECHO_METADATA REBASE: the old prose re-parse (extractQuantityMentions /
 * hasMultiIntentText / extractStatedVolume / extractStatedDuration, all
 * regex sweeps over free text) is DELETED. SARV and SARD now read ONLY
 * echo_metadata. ANSWERS-STEP BINDING RULE: the headline answered
 * quantity/dates can bind to a SINGLE intent only.
 *   - echo_metadata absent, malformed, or describing more than one
 *     procurement intent -> both checks stand down (silent) - multi-intent
 *     requirements are represented per-line at Lines (a future batch), not
 *     validated here.
 *   - Within the bound (sole) intent: quantities/durations array length !=
 *     1 (nothing stated, or multiple/ambiguous stated mentions) -> that
 *     check stands down; the OTHER check is independent and still runs.
 *
 * Two soft rules, MEDIUM severity, mirrored in
 * backend/routes/_stated_intent_validation_rules.py (Answers-submit only -
 * unlike the date rules, there is no Lines/Gate/Resume consumer of these
 * two; the backend mirror exists for parity, same precedent as SANT/SAST
 * in _date_validation_rules.py being computed there with no live caller
 * yet):
 *
 *   SARV - Stated-vs-Answered Volume. Stated source: the bound intent's
 *     sole echo_metadata quantity entry. Fires when the answered quantity
 *     is present, its unit is "comparable" (see isComparableAnsweredUnit),
 *     and its value differs from the stated one.
 *
 *   SARD - Stated-vs-Answered Duration. Stated source: the bound intent's
 *     sole echo_metadata duration entry, canonicalized to Month/Week/Year.
 *     Fires when start+end are answered and the date span (in the stated
 *     unit) mismatches the stated value beyond the existing whole-unit
 *     rounding tolerance (deriveDurationFromDates mirrors
 *     quantityComposite.js's deriveCalendarQuantityFromDates exactly -
 *     Month=30 days, Week=7 days - extended with Year=365, since a stated
 *     duration may be expressed in years even though the quantity
 *     composite input itself never offers that unit).
 */
import { parseQuantityText, CALENDAR_DURATION_UNITS, collapseMultiWordAliases } from './quantityComposite';
import { toIsoDateOrNull } from './dateValidationRules';

const durationUnitFromWord = (word) => {
  const w = String(word || '').toLowerCase();
  if (w.startsWith('month')) return 'Month';
  if (w.startsWith('week')) return 'Week';
  if (w.startsWith('year')) return 'Year';
  return null;
};

// Month=30/Week=7 mirrors quantityComposite.js's deriveCalendarQuantityFromDates
// exactly (same simple, deterministic day-count convention); Year=365 is
// new here - the quantity composite input never offers "Year" as a unit,
// but a stated duration recorded in echo_metadata legitimately can be.
const DAYS_PER_UNIT = { Month: 30, Week: 7, Year: 365 };

const singularize = (word) => {
  const w = String(word || '').toLowerCase();
  return w.endsWith('s') && w.length > 1 ? w.slice(0, -1) : w;
};

/**
 * Unit default from stated volume (defect fix): tolerant (case/plural-
 * insensitive) match of a stated raw unit word (e.g. "consultants") against
 * uom_master's uom_name list - the exact-cased master name if a
 * singular/plural-tolerant match exists, else null. Used to default the
 * quantity composite's unit to the stated NON-calendar volume's own unit
 * (SmartAnswerInput.jsx's QuantityCompositeInput) instead of leaving a
 * services quantity question's unit empty/stuck on whatever a stray
 * default_value happened to seed - division of labor: duration
 * consistency is checked from start/end dates (SARD); volume consistency
 * from the quantity answer (SARV) - the composite itself must never steer
 * a volume question into a calendar unit (Month/Week).
 */
export const matchStatedUnitAgainstMaster = (rawUnitWord, uoms = []) => {
  if (!rawUnitWord) return null;
  // MIXED-SEMANTICS + ECHO FIX BATCH (item F trace): collapse multi-word
  // area aliases ("sq ft" -> "sqft") BEFORE singularize/compare, same as
  // quantityComposite.js's own matchUnitWord already does - without this,
  // an echo_metadata unit of "sq ft" (space) never matched master's
  // "Sqft", silently leaving the stated-unit default blank.
  const target = singularize(collapseMultiWordAliases(rawUnitWord));
  const found = (uoms || []).find((u) => singularize(collapseMultiWordAliases(u.uom_name)) === target);
  return found ? found.uom_name : null;
};

/**
 * Is the ANSWERED quantity's unit comparable to a stated (non-calendar)
 * volume? data/uom_master.csv has no headcount unit at all ("Consultant"
 * doesn't exist) - a resolved answered unit (e.g. "Engagement", "Each") is
 * treated as comparable by default as long as it ISN'T itself a
 * calendar-duration unit (that would mean the answered quantity is
 * describing a duration, not a volume - a different dimension entirely,
 * SARD's territory, never compared here). When the answered text doesn't
 * resolve to any uom_master unit at all (a legacy/unnormalized answer),
 * falls back to a tolerant (case/plural-insensitive) raw-word match
 * against the stated unit word instead of assuming comparability blind.
 */
export const isComparableAnsweredUnit = (answeredUom, answeredRawText, statedRawUnitWord) => {
  if (answeredUom) return !CALENDAR_DURATION_UNITS.includes(answeredUom);
  const words = String(answeredRawText || '').match(/[a-zA-Z]+/g) || [];
  if (words.length === 0) return true; // bare number, nothing to contradict
  if (!statedRawUnitWord) return true;
  return singularize(words[words.length - 1]) === singularize(statedRawUnitWord);
};

/**
 * ANSWERS-STEP BINDING RULE: the headline answered quantity/dates can bind
 * to a SINGLE intent only. Returns that sole intent object, or null when
 * echoMetadata is absent/malformed or describes more than one procurement
 * intent (multi-intent requirements are validated per-line at Lines - a
 * future batch, not here).
 */
export const bindEchoIntent = (echoMetadata) => {
  const intents = echoMetadata?.intents;
  if (!Array.isArray(intents) || intents.length !== 1) return null;
  const intent = intents[0];
  return intent && typeof intent === 'object' ? intent : null;
};

/** SARV's stated side. null (stand down): no bound intent, no stated
 * quantity, more than one stated quantity mention (ambiguous), or the
 * stated unit is itself calendar-flavored (a duration, not a volume -
 * SARD's territory, never SARV's - CVC guard). */
export const boundStatedVolume = (echoMetadata) => {
  const intent = bindEchoIntent(echoMetadata);
  if (!intent) return null;
  const quantities = intent.quantities;
  if (!Array.isArray(quantities) || quantities.length !== 1) return null;
  const stated = quantities[0];
  if (!stated || typeof stated.value !== 'number' || !stated.evidence) return null;
  if (stated.unit && durationUnitFromWord(stated.unit)) return null;
  return { value: stated.value, rawUnitWord: stated.unit || null, evidence: stated.evidence };
};

/** SARD's stated side. null (stand down): no bound intent, no stated
 * duration, more than one stated duration mention (ambiguous), or the
 * stated unit doesn't canonicalize to a supported calendar unit
 * (Month/Week/Year). */
export const boundStatedDuration = (echoMetadata) => {
  const intent = bindEchoIntent(echoMetadata);
  if (!intent) return null;
  const durations = intent.durations;
  if (!Array.isArray(durations) || durations.length !== 1) return null;
  const stated = durations[0];
  if (!stated || typeof stated.value !== 'number' || !stated.evidence) return null;
  const unit = durationUnitFromWord(stated.unit);
  if (!unit) return null;
  return { value: stated.value, unit, evidence: stated.evidence };
};

/** Whole-unit count between two ISO dates for Month/Week/Year, rounded to
 * the nearest whole number - same tolerance convention as
 * quantityComposite.js's deriveCalendarQuantityFromDates. null when the
 * unit is unsupported, either date is missing/invalid, or the span isn't
 * positive. Never throws. */
export const deriveDurationFromDates = (startIso, endIso, unit) => {
  const daysPerUnit = DAYS_PER_UNIT[unit];
  if (!daysPerUnit) return null;
  const start = toIsoDateOrNull(startIso);
  const end = toIsoDateOrNull(endIso);
  if (!start || !end) return null;
  const days = (new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24);
  if (days <= 0) return null;
  const rounded = Math.round(days / daysPerUnit);
  return rounded > 0 ? rounded : null;
};

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Independently duplicated (not imported from lib/utils.js) - same "mirrors
// X, doesn't depend on it" convention dateValidationRules.js's own
// formatDisplayDate already uses, for the same reason (keep this module's
// import surface limited to what it already needs).
const formatDisplayDate = (iso) => {
  const parsed = toIsoDateOrNull(iso);
  if (!parsed) return '';
  const [year, month, day] = parsed.split('-');
  const monthIndex = parseInt(month, 10) - 1;
  if (monthIndex < 0 || monthIndex > 11) return parsed;
  return `${day}-${MONTH_ABBR[monthIndex]}-${year}`;
};

const pluralize = (value, unit) => `${value} ${unit}${value === 1 ? '' : 's'}`;

// No numeric value is shown alongside SARU's unit names (the alert is about
// the DIMENSION change, not a count), so it always renders the plain plural
// form of the unit name itself.
const pluralizeUnitName = (unit) => (unit.endsWith('s') ? unit : `${unit}s`);

/** SARV - see module doc. Returns a fully-built rule result or null
 * (silent): no bound single-intent stated volume, no/incomparable answered
 * quantity, or matching values. */
export const checkStatedVolumeEcho = ({ echoMetadata, answeredQuantityText, uoms = [] }) => {
  const stated = boundStatedVolume(echoMetadata);
  if (!stated) return null;
  if (!answeredQuantityText) return null;
  const { value: answeredValueRaw, uom: answeredUom } = parseQuantityText(answeredQuantityText, uoms);
  if (!answeredValueRaw) return null;
  if (!isComparableAnsweredUnit(answeredUom, answeredQuantityText, stated.rawUnitWord)) return null;
  const answeredValue = Number(answeredValueRaw);
  if (answeredValue === stated.value) return null;
  return {
    code: 'SARV',
    severity: 'MEDIUM',
    statedValue: stated.value,
    statedUnitWord: stated.rawUnitWord || 'unit(s)',
    answeredValue,
    message: `Your initial stated requirement mentions '${stated.evidence}'; you have entered ${answeredValue}. Confirm which is intended.`,
  };
};

/**
 * SARU REDESIGN (2026-07-27 ruling) - looks up a uom_master row's
 * measurement_family/unit_semantics by exact uom_name match. FAIL-SAFE
 * TO FIRE: a unit missing from the master entirely, or matched to a row
 * that predates this schema (blank unit_semantics), defaults to
 * unit_semantics 'procurement_object' - which never qualifies for the
 * measurement+same-family silence exemption below, so an unclassified
 * unit change always surfaces for confirmation rather than passing
 * through silently while master data is still being migrated onto this
 * schema. (The old UOM_DIMENSION_GROUPS map still exists, for
 * rankUomsForSuggestion's display-only tiering - SARU no longer depends
 * on it.)
 */
const findUomSemantics = (uoms, uomName) => {
  const row = (uoms || []).find((u) => u.uom_name === uomName);
  const unitSemantics = String(row?.unit_semantics || '').trim().toLowerCase() || 'procurement_object';
  const measurementFamily = String(row?.measurement_family || '').trim().toLowerCase() || null;
  return { unitSemantics, measurementFamily };
};

/**
 * SARU - data-driven cross-unit semantics (SARU REDESIGN, 2026-07-27
 * ruling, supersedes the old UOM_DIMENSION_GROUPS coarse-dimension
 * compare). Unlike SARV/SARD (services-only at the StepAnswers.jsx call
 * site), SARU runs for ALL procurement types - a unit change that alters
 * or obscures the procurement object is a data-integrity concern
 * regardless of what's being procured, not a services-specific echo.
 *
 * Ruling: a unit change stays silent ONLY when it's within a
 * mathematically convertible measurement family (Sqft<->Sqm, Kg<->Metric
 * Tonne) - both sides' uom_master row must have unit_semantics=
 * "measurement" AND the same non-empty measurement_family. ANY other
 * change fires: procurement_object<->procurement_object (Consultant->
 * License), procurement_object<->generic_count in either direction
 * (Consultant->Each, Each->Consultant), a cross-family measurement change
 * (Sqft->Kg), and any transition to/from a temporal unit (Consultant->
 * Month - the sole guard for that flip since the derive-quantity-from-
 * dates auto-fill was removed; a CALENDAR answered unit is NOT exempted).
 *
 * null (silent) also when: no bound single-intent stated volume, no
 * answered quantity/unit, or the stated and answered units are literally
 * the same uom_master name (no change to flag) - checked BEFORE
 * semantics, so a no-op never fires regardless of the unit's
 * classification. An unmapped/pre-schema unit is NOT silent - see
 * findUomSemantics's fail-safe-to-fire default. De-dup with SARV is the
 * CALLER's job (see StepAnswers.jsx): when SARU fires, it is the more
 * specific/root-cause explanation of the same underlying fact a naive
 * SARV value-mismatch would also flag, so the caller checks SARU first
 * and skips SARV when it fires.
 */
export const checkStatedUnitDimensionEcho = ({ echoMetadata, answeredQuantityText, uoms = [] }) => {
  const stated = boundStatedVolume(echoMetadata);
  if (!stated || !stated.rawUnitWord) return null;
  if (!answeredQuantityText) return null;
  const { uom: answeredUom } = parseQuantityText(answeredQuantityText, uoms);
  if (!answeredUom) return null;
  const statedMasterUnit = matchStatedUnitAgainstMaster(stated.rawUnitWord, uoms) || stated.rawUnitWord;
  if (statedMasterUnit === answeredUom) return null;
  const statedMeta = findUomSemantics(uoms, statedMasterUnit);
  const answeredMeta = findUomSemantics(uoms, answeredUom);
  const silentConvertible =
    statedMeta.unitSemantics === 'measurement' &&
    answeredMeta.unitSemantics === 'measurement' &&
    statedMeta.measurementFamily &&
    statedMeta.measurementFamily === answeredMeta.measurementFamily;
  if (silentConvertible) return null;
  return {
    code: 'SARU',
    severity: 'MEDIUM',
    statedUnit: statedMasterUnit,
    answeredUnit: answeredUom,
    message: `You originally specified a quantity in ${pluralizeUnitName(statedMasterUnit)}. You have changed the unit to ${pluralizeUnitName(answeredUom)}. Please confirm this represents a change in the procurement requirement.`,
  };
};

/** SARD - see module doc. Returns a fully-built rule result or null
 * (silent): no bound single-intent stated duration, dates not
 * answered/invalid, or the derived span matches the stated value. */
export const checkStatedDurationEcho = ({ echoMetadata, startIso, endIso }) => {
  const stated = boundStatedDuration(echoMetadata);
  if (!stated) return null;
  const derived = deriveDurationFromDates(startIso, endIso, stated.unit);
  if (derived === null) return null;
  if (derived === stated.value) return null;
  return {
    code: 'SARD',
    severity: 'MEDIUM',
    statedValue: stated.value,
    statedUnit: stated.unit,
    derivedValue: derived,
    startIso,
    endIso,
    message: `Your initial stated requirement says '${stated.evidence}', but your engagement dates (${formatDisplayDate(startIso)} – ${formatDisplayDate(endIso)}) span approximately ${pluralize(derived, stated.unit)}. Confirm which is intended, or update your dates.`,
  };
};

/**
 * ATTRIBUTION + DE-DUP (item 3): the existing typed-quantity-vs-dates
 * check (StepAnswers.jsx's CALENDAR_DURATION_UNITS gate, comparing the
 * ANSWERED quantity directly against the dates) and SARD can describe the
 * SAME underlying contradiction when the user's typed quantity value
 * equals the stated duration - that's one fact, not two. When this
 * returns true, the caller must show SARD's requirement-attributed
 * message INSTEAD of the generic "Quantity says..." one - one dialog,
 * never both. General rule (per the ticket): when a stated-intent check
 * and a typed-value check would flag the same contradiction, the
 * stated-requirement attribution wins.
 */
export const sardAttributionForQuantityMismatch = ({ qtyNum, qtyUom, statedDuration }) => {
  if (!statedDuration) return false;
  if (!CALENDAR_DURATION_UNITS.includes(qtyUom)) return false;
  return Number(qtyNum) === statedDuration.value;
};
